/** @jsxImportSource @emotion/react */
import 'quill/dist/quill.snow.css';

import { SerializedStyles } from '@emotion/react';
import Quill from 'quill';
import { useEffect, useRef } from 'react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  modules: Record<string, unknown>;
  css?: SerializedStyles;
}

const QuillEditor = ({ value, onChange, modules, css: cssProp }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Quill | null>(null);
  const valueRef = useRef(value);

  useEffect(() => {
    if (!containerRef.current) return;

    const editor = new Quill(containerRef.current, {
      theme: 'snow',
      modules
    });

    editorRef.current = editor;

    if (valueRef.current) {
      editor.clipboard.dangerouslyPasteHTML(valueRef.current);
    }

    editor.on('text-change', () => {
      const html = editor.root.innerHTML;
      valueRef.current = html;
      onChange(html);
    });

    const container = containerRef.current;
    return () => {
      editorRef.current = null;
      container?.querySelector('.ql-toolbar')?.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (value !== valueRef.current) {
      valueRef.current = value;
      const selection = editor.getSelection();
      editor.clipboard.dangerouslyPasteHTML(value);
      if (selection) editor.setSelection(selection);
    }
  }, [value]);

  return <div ref={containerRef} css={cssProp} />;
};

export default QuillEditor;
